import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchPermissionData } from "../../../Counter/permissionData";
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { DependentMultiSelectDropdown } from '../../../Utils/multiSelectDropdown';
import { fetchCategoryDataFilterV2, fetchOriginDataFilterV2 } from '../../../Utils/helper';
import ArrivalBreakdownTable, { ARRIVAL_BREAKDOWN_COLUMNS } from './ArrivalBreakdownTable';
import NegativeEstimateStockTable, { NEGATIVE_ESTIMATE_STOCK_COLUMNS } from './NegativeEstimateStockTable';
import CustomerReserveDetailsTable, { CUSTOMER_RESERVE_DETAILS_COLUMNS } from './CustomerReserveDetailsTable';
import NotificationPanel from '../../Notifications/NotificationPanel';

const Dashboard = () => {
  const dispatch = useDispatch();

  // Load saved customizations from localStorage
  const loadCustomizations = (tableKey) => {
    const saved = localStorage.getItem(`dashboard_${tableKey}_customizations`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Save customizations to localStorage
  const saveCustomizations = (tableKey, customizations) => {
    localStorage.setItem(`dashboard_${tableKey}_customizations`, JSON.stringify(customizations));
  };

  // Initialize state for each table separately
  const initializeTableState = (tableKey, defaultColumns) => {
    const saved = loadCustomizations(tableKey);
    if (saved) {
      return {
        visibleColumns: saved.visibleColumns || defaultColumns.reduce((acc, col) => {
          acc[col.key] = true;
          return acc;
        }, {})
      };
    }
    return {
      visibleColumns: defaultColumns.reduce((acc, col) => {
        acc[col.key] = true;
        return acc;
      }, {})
    };
  };

  // Common date range state - fixed start date (today), end date for coming days
  const loadDateRange = () => {
    const today = dayjs().format('YYYY-MM-DD');
    const saved = localStorage.getItem('dashboard_dateRange');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Always use today as start date, but keep saved end date if valid
        const savedEndDate = parsed.endDate;
        const endDate = savedEndDate && dayjs(savedEndDate).isAfter(dayjs(), 'day') 
          ? savedEndDate 
          : dayjs().add(7, 'day').format('YYYY-MM-DD');
        return {
          startDate: today, // Always today
          endDate: endDate
        };
      } catch (e) {
        return {
          startDate: today,
          endDate: dayjs().add(7, 'day').format('YYYY-MM-DD')
        };
      }
    }
    return {
      startDate: today,
      endDate: dayjs().add(7, 'day').format('YYYY-MM-DD')
    };
  };

  const [dateRange, setDateRange] = useState(loadDateRange);
  // selectedCategoryIds - Array of category IDs (if empty, means ALL categories)
  // selectedOriginIds - Array of origin IDs (if empty, means ALL origins - this is accessory to category)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedOriginIds, setSelectedOriginIds] = useState([]);

  // Calculate days from date range
  const calculateDays = () => {
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    return end.diff(start, 'day');
  };

  const days = calculateDays();

  // Arrival Breakdown table state
  const [arrivalBreakdownState, setArrivalBreakdownState] = useState(() => initializeTableState('arrivalBreakdown', ARRIVAL_BREAKDOWN_COLUMNS));

  // Negative Estimate Stock table state
  const [negativeEstimateStockState, setNegativeEstimateStockState] = useState(() => initializeTableState('negativeEstimateStock', NEGATIVE_ESTIMATE_STOCK_COLUMNS));

  // Customer Reserve Details table state
  const [customerReserveDetailsState, setCustomerReserveDetailsState] = useState(() => initializeTableState('customerReserveDetails', CUSTOMER_RESERVE_DETAILS_COLUMNS));

  // Table visibility states
  const [tableVisibility, setTableVisibility] = useState(() => {
    const saved = localStorage.getItem('dashboard_table_visibility');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {
          arrivalBreakdown: true,
          negativeEstimateStock: true,
          customerReserveDetails: true
        };
      }
    }
    return {
      arrivalBreakdown: true,
      negativeEstimateStock: true,
      customerReserveDetails: true
    };
  });

  const toggleTableVisibility = (tableKey) => {
    setTableVisibility(prev => {
      const newState = {
        ...prev,
        [tableKey]: !prev[tableKey]
      };
      localStorage.setItem('dashboard_table_visibility', JSON.stringify(newState));
      return newState;
    });
  };

  // Saved combinations state
  const [savedCombinations, setSavedCombinations] = useState([]);
  const [isFilterSettingsOpen, setIsFilterSettingsOpen] = useState(false);
  const [editingCombination, setEditingCombination] = useState(null); // For edit modal state
  const [tempFilterSettings, setTempFilterSettings] = useState({
    name: '',
    categoryIds: [],
    originIds: [],
    categoryLabels: [],
    originLabels: []
  });

  // Load saved combinations from localStorage
  const loadSavedCombinations = () => {
    const saved = localStorage.getItem('dashboard_saved_filter_combinations');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  // Save combinations to localStorage
  const saveSavedCombinations = (combinations) => {
    localStorage.setItem('dashboard_saved_filter_combinations', JSON.stringify(combinations));
    setSavedCombinations(combinations);
  };

  useEffect(() => {
    dispatch(fetchPermissionData());
    
    // Load saved combinations on mount
    const saved = loadSavedCombinations();
    setSavedCombinations(saved);

    // Categories and Origins will not load any default values anymore as per request
    setSelectedCategoryIds([]);
    setSelectedOriginIds([]);
  }, [dispatch]);

  // Handler for end date change - start date is always fixed to today
  const handleEndDateChange = (date) => {
    if (date) {
      const today = dayjs();
      const startDate = today.format('YYYY-MM-DD'); // Always today
      const endDate = date.format('YYYY-MM-DD');
      
      // Ensure end date is not before today
      if (dayjs(endDate).isBefore(today, 'day')) {
        return;
      }
      
      const newDateRange = { startDate, endDate };
      setDateRange(newDateRange);
      localStorage.setItem('dashboard_dateRange', JSON.stringify(newDateRange));
    }
  };

  // Common handler for column visibility change - uses table type to determine which state to update
  const handleColumnVisibilityChange = (tableType) => (columnKey) => {
    let tableKey;
    let setState;
    
    switch (tableType) {
      case 'arrivalBreakdown':
        tableKey = 'arrivalBreakdown';
        setState = setArrivalBreakdownState;
        break;
      case 'negativeEstimateStock':
        tableKey = 'negativeEstimateStock';
        setState = setNegativeEstimateStockState;
        break;
      case 'customerReserveDetails':
        tableKey = 'customerReserveDetails';
        setState = setCustomerReserveDetailsState;
        break;
      default:
        return;
    }
    
    // Use functional update to ensure we have the latest state
    setState((prevState) => {
      const newVisibleColumns = {
        ...prevState.visibleColumns,
        [columnKey]: !prevState.visibleColumns[columnKey]
      };
      const newState = { ...prevState, visibleColumns: newVisibleColumns };
      saveCustomizations(tableKey, newState);
      return newState;
    });
  };

  return (
    <div className="RightSideDiv">
      <div className="dashboard-main">
        <div className="row">
          <div className="col-sm-9">
            {/* Common Date Range Picker and Filters - Top Section */}
            <div className="dashbord-top-filter" style={{ 
              padding: '10px', 
              background: '#fff', 
              borderRadius: '10px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
             marginBottom: '15px',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '80px' }}>
                  Date Range:
                </label>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  padding: '4px 11px',
                  background: '#fff'
                }}>
                  {/* Fixed Start Date (Today) */}
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#000',
                    padding: '0 8px',
                    // borderRight: '1px solid #d9d9d9',
                    // marginRight: '8px'
                  }}>
                    {dayjs().format('YYYY-MM-DD')}
                  </span>
                  
                  {/* Separator */}
                  <span style={{ color: '#d9d9d9', margin: '0 4px' }}>-</span>
                  
                  {/* End Date Picker */}
                  <DatePicker
                    onChange={handleEndDateChange}
                    value={dateRange.endDate ? dayjs(dateRange.endDate) : dayjs().add(7, 'day')}
                    allowClear={false}
                    disabledDate={(current) => {
                      // Disable dates before today
                      return current && current < dayjs().startOf('day');
                    }}
                    style={{ border: 'none', padding: 0 }}
                    format="YYYY-MM-DD"
                  />
                </div>
                {/* <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                  Coming {days} days
                </span> */}
              </div>

              {/* Category Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '100px' }}>
                  Category:
                </label>
                <DependentMultiSelectDropdown
                  categoryData={[]}
                  selectedValues={selectedCategoryIds}
                  setSelectedValues={(selected) => {
                    setSelectedCategoryIds(selected);
                    // API calls are automatically triggered via useEffect when filters change
                  }}
                  placeholder='Select Categories...'
                  noOptionsMessage='No categories available'
                  setCurrentPage={() => {}}
                  setCurrentPinkSectionPage={() => {}}
                  fetchOptionsApi={fetchCategoryDataFilterV2}
                  dependentFilters={{
                    origin_ids: selectedOriginIds,
                    // Add other dependent filters as needed for API
                  }}
                />
              </div>

              {/* Origin Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '80px' }}>
                  Origin:
                </label>
                <DependentMultiSelectDropdown
                  categoryData={[]}
                  selectedValues={selectedOriginIds}
                  setSelectedValues={(selected) => {
                    setSelectedOriginIds(selected);
                  }}
                  placeholder="Select origin..."
                  noOptionsMessage='No origin available'
                  setCurrentPage={() => {}}
                  setCurrentPinkSectionPage={() => {}}
                  fetchOptionsApi={fetchOriginDataFilterV2}
                  dependentFilters={{
                    category_ids: selectedCategoryIds,
                  }}
                />
              </div>

              {/* Saved Filters Dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '100px' }}>
                  Saved Filters:
                </label>
                <select
                  onChange={(e) => {
                    const combinationId = e.target.value;
                    if (combinationId) {
                      const selected = savedCombinations.find(c => c.id === combinationId);
                      if (selected) {
                        setSelectedCategoryIds(selected.categoryIds || []);
                        setSelectedOriginIds(selected.originIds || []);
                      }
                    } else {
                      setSelectedCategoryIds([]);
                      setSelectedOriginIds([]);
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d9d9d9',
                    fontSize: '14px',
                    minWidth: '200px',
                    background: '#fff'
                  }}
                  defaultValue=""
                >
                  <option value="">Select Saved Filter...</option>
                  {savedCombinations.map((combo) => (
                    <option key={combo.id} value={combo.id}>
                      {combo.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter Settings Icon */}
              <div className="filter-setting" style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    setTempFilterSettings({
                      name: '',
                      categoryIds: selectedCategoryIds,
                      originIds: selectedOriginIds,
                      categoryLabels: [], // Labels might not be available here yet, manual name is preferred anyway
                      originLabels: []
                    });
                    setEditingCombination(null);
                    setIsFilterSettingsOpen(true);
                  }}
                  style={{
                    background: '#F2F7F6',
                    color: '#006E47',
                    border: 'none',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '5px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  title="Set default filters"
                >
                  <img src={window.getPublicUrl("/assets/images/setting.svg")} alt="Filter Settings"/>
                </button>
              </div>
            </div>

            {/* Arrival Breakdown: Coming XX days [Arrival] breakdown */}
            <ArrivalBreakdownTable
              days={days}
              visibleColumns={arrivalBreakdownState.visibleColumns}
              onColumnVisibilityChange={handleColumnVisibilityChange('arrivalBreakdown')}
              dateRange={dateRange}
              selectedCategoryIds={selectedCategoryIds}
              selectedOriginIds={selectedOriginIds}
              isVisible={tableVisibility.arrivalBreakdown}
              onToggleVisibility={() => toggleTableVisibility('arrivalBreakdown')}
            />

            {/* Negative Estimate Stock: Coming XX days [Direct in/out] & [Prepack in/out] estimate stock = negative */}
            <NegativeEstimateStockTable
              days={days}
              visibleColumns={negativeEstimateStockState.visibleColumns}
              onColumnVisibilityChange={handleColumnVisibilityChange('negativeEstimateStock')}
              dateRange={dateRange}
              selectedCategoryIds={selectedCategoryIds}
              selectedOriginIds={selectedOriginIds}
              isVisible={tableVisibility.negativeEstimateStock}
              onToggleVisibility={() => toggleTableVisibility('negativeEstimateStock')}
            />

            {/* Customer Reserve Details: Coming XX days [Direct in/out] & [Prepack in/out] details of customer reserve quantity */}
            <CustomerReserveDetailsTable
              days={days}
              visibleColumns={customerReserveDetailsState.visibleColumns}
              onColumnVisibilityChange={handleColumnVisibilityChange('customerReserveDetails')}
              dateRange={dateRange}
              selectedCategoryIds={selectedCategoryIds}
              selectedOriginIds={selectedOriginIds}
              isVisible={tableVisibility.customerReserveDetails}
              onToggleVisibility={() => toggleTableVisibility('customerReserveDetails')}
            />
          </div>
          
          {/* Notification Panel - Right Side */}
          <div className="col-sm-3">
            <NotificationPanel />
          </div>
        </div>
      </div>

      {/* Filter Settings Modal */}
      {isFilterSettingsOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setIsFilterSettingsOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '10px',
              padding: '24px',
              width: '600px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#006E47', fontSize: '20px' }}>
                {editingCombination ? 'Edit Saved Filter' : 'Manage Saved Filters'}
              </h3>
              <button 
                onClick={() => setIsFilterSettingsOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#999' }}
              >
                ×
              </button>
            </div>

            {/* List of existing combinations (only shown if not currently editing one) */}
            {!editingCombination && savedCombinations.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '15px', fontWeight: 600 }}>
                  Your Saved Filters:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {savedCombinations.map(combo => (
                    <div key={combo.id} style={{ 
                      padding: '10px 15px', 
                      background: '#f9f9f9', 
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #eee'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: 500 }}>{combo.name}</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => {
                            setEditingCombination(combo);
                            setTempFilterSettings({
                              name: combo.name,
                              categoryIds: combo.categoryIds,
                              originIds: combo.originIds,
                              categoryLabels: combo.categoryLabels || [],
                              originLabels: combo.originLabels || []
                            });
                          }}
                          style={{
                            padding: '4px 10px',
                            background: '#006E47',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this filter?')) {
                              saveSavedCombinations(savedCombinations.filter(c => c.id !== combo.id));
                            }
                          }}
                          style={{
                            padding: '4px 10px',
                            background: '#ff4d4f',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ padding: '15px', border: '1px solid #e8e8e8', borderRadius: '8px', background: '#fafafa' }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>
                {editingCombination ? 'Edit Filter Details' : 'Add New Filter Combination'}
              </h4>
              
              {/* Filter Name */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Filter Name:
                </label>
                <input
                  type="text"
                  value={tempFilterSettings.name}
                  onChange={(e) => setTempFilterSettings(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter a name"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '5px',
                    border: '1px solid #d9d9d9',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Category Filter */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Categories:
                </label>
                <DependentMultiSelectDropdown
                  categoryData={[]}
                  selectedValues={tempFilterSettings.categoryIds}
                  setSelectedValues={(selected, options) => {
                    setTempFilterSettings(prev => ({
                      ...prev,
                      categoryIds: selected,
                      categoryLabels: options ? options.map(o => o.label) : []
                    }));
                  }}
                  placeholder='Select categories...'
                  noOptionsMessage='No categories available'
                  setCurrentPage={() => {}}
                  setCurrentPinkSectionPage={() => {}}
                  fetchOptionsApi={fetchCategoryDataFilterV2}
                  dependentFilters={{
                    origin_ids: tempFilterSettings.originIds,
                  }}
                />
              </div>

              {/* Origin Filter */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Origins:
                </label>
                <DependentMultiSelectDropdown
                  categoryData={[]}
                  selectedValues={tempFilterSettings.originIds}
                  setSelectedValues={(selected, options) => {
                    setTempFilterSettings(prev => ({
                      ...prev,
                      originIds: selected,
                      originLabels: options ? options.map(o => o.label) : []
                    }));
                  }}
                  placeholder="Select origins..."
                  noOptionsMessage='No origins available'
                  setCurrentPage={() => {}}
                  setCurrentPinkSectionPage={() => {}}
                  fetchOptionsApi={fetchOriginDataFilterV2}
                  dependentFilters={{
                    category_ids: tempFilterSettings.categoryIds,
                  }}
                />
              </div>

              {/* Save/Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                {editingCombination && (
                  <button
                    onClick={() => {
                      setEditingCombination(null);
                      setTempFilterSettings({ name: '', categoryIds: selectedCategoryIds, originIds: selectedOriginIds, categoryLabels: [], originLabels: [] });
                    }}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d9d9d9',
                      background: 'white',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  onClick={() => {
                    if (tempFilterSettings.categoryIds.length === 0 && tempFilterSettings.originIds.length === 0) {
                      toast.error('Please select at least one category or origin.');
                      return;
                    }
                    let finalName = tempFilterSettings.name.trim();
                    if (!finalName) {
                      const catPart = tempFilterSettings.categoryLabels.length > 0 
                        ? tempFilterSettings.categoryLabels.join(', ') 
                        : (tempFilterSettings.categoryIds.length > 0 ? 'Selected Categories' : '');
                      
                      const originPart = tempFilterSettings.originLabels.length > 0 
                        ? tempFilterSettings.originLabels.join(', ') 
                        : (tempFilterSettings.originIds.length > 0 ? 'Selected Origins' : '');
                      
                      if (catPart && originPart) {
                        finalName = `${catPart} | ${originPart}`;
                      } else {
                        finalName = catPart || originPart || 'New Filter';
                      }
                    }
                    
                    if (editingCombination) {
                      const updated = savedCombinations.map(c => 
                        c.id === editingCombination.id 
                          ? { ...tempFilterSettings, name: finalName, id: c.id } 
                          : c
                      );
                      saveSavedCombinations(updated);
                      setEditingCombination(null);
                    } else {
                      const newCombo = {
                        ...tempFilterSettings,
                        name: finalName,
                        id: Date.now().toString()
                      };
                      saveSavedCombinations([...savedCombinations, newCombo]);
                    }
                    
                    // Reset temp settings
                    setTempFilterSettings({ name: '', categoryIds: [], originIds: [], categoryLabels: [], originLabels: [] });
                  }}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    background: '#006E47',
                    color: 'white',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  {editingCombination ? 'Update Filter' : 'Save as New Filter'}
                </button>
              </div>
            </div>

            {/* <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setIsFilterSettingsOpen(false)}
                style={{
                  padding: '8px 20px',
                  background: 'transparent',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close Manager
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
